import { computed, ref } from "vue";
import { defineStore } from "pinia";
import partnerSubscriptions from "@shared/services/partners/partnerSubscriptions/partnerSubscriptions.ts";
import { usePartnerStore } from "@shared/stores/partner/partner.ts";
import logger from "@shared/utils/logger.ts";
import { ResponseType as PartnerSubscriptionResponse } from "@shared/services/partners/partnerSubscription/response.ts";
import partnerSubscription from "@shared/services/partners/partnerSubscription/partnerSubscription.ts";
import entitlementPerSubscriptionUsage from "@shared/services/partners/EntitlementPerSubscriptionUsage/entitlementPerSubscriptionUsage.ts";
import { DateTime } from "luxon";
import { FilterCardsListModel } from "@shared/components/filter-cards/filter-card/types";
import { calcPercentage } from "@shared/stores/partner/calculatePercentage.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { messagesService } from "@shared/services/messages.ts";
import {
  RadioDeploymentType,
  RenewalPeriodValues,
} from "@shared/types/localTypes/partner-portal.ts";
import { SubscriptionStatus } from "@shared/types/localTypes/subscription.ts";
import { analyticsService } from "@shared/services/analytics.ts";

export type PartnerTableData = {
  companyName: string;
  subscriptionId: number;
  subscriptionType: string;
  derivedEndDate: string;
  primaryContactEmail: string;
  subscriptionStatus: string;
  monthlyPercentage: number | null;
  region: string;
  paymentType: string;
  deploymentType: string;
  billingView: boolean;
  billingType: string;
};

export const usePartnerTableStore = defineStore("partnerTable", () => {
  // State
  const _isLoading = ref<boolean>(false);
  const _isLoadingSubscription = ref<boolean>(false);
  const _tableData = ref<PartnerTableData[]>([]);
  const _filterBy = ref<FilterCardsListModel>({});
  const _subscriptionDetails = ref<PartnerSubscriptionResponse | null>(null);
  const _selectedSubscriptionId = ref<number | null>(null);
  const _selectedRadio = ref<RadioDeploymentType>(
    RadioDeploymentType.UNSPECIFIED,
  );

  // Getters
  const tableData = computed(() => _tableData.value);
  const totalSubscriptions = computed(() => _tableData.value.length);
  const isLoading = computed(() => _isLoading.value);
  const isLoadingSubscription = computed(() => _isLoadingSubscription.value);
  const subscriptionDetails = computed(() => _subscriptionDetails.value);
  const saasSubscriptions = computed(
    () =>
      _tableData.value.filter((subscription) => {
        return subscription.deploymentType.toLowerCase() === "saas";
      }).length,
  );
  const activeSaasSubscriptions = computed(
    () =>
      _tableData.value.filter(
        (subscription) =>
          subscription.subscriptionStatus === SubscriptionStatus.Active &&
          subscription.deploymentType.toLowerCase() === "saas",
      ).length,
  );
  const selfHostedSubscriptions = computed(
    () =>
      _tableData.value.filter(
        (item) => item.deploymentType.toLowerCase() === "self_hosted",
      ).length,
  );
  const activeSelfHostedSubscriptions = computed(
    () =>
      _tableData.value.filter(
        (subscription) =>
          subscription.subscriptionStatus === SubscriptionStatus.Active &&
          subscription.deploymentType.toLowerCase() === "self_hosted",
      ).length,
  );
  const subscriptionTypes = computed(() => {
    return _tableData.value.reduce(
      (acc, subscription) => {
        const subscriptionType = subscription.subscriptionType;

        acc[subscriptionType] = acc[subscriptionType]
          ? ++acc[subscriptionType]
          : 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  });
  const selectedSubscriptionId = computed(
    (): number | null => _selectedSubscriptionId.value,
  );
  const selectedTableSubscription = computed(() => {
    const selectedSubscriptionIdValue = _selectedSubscriptionId.value;
    if (!selectedSubscriptionIdValue) {
      return null;
    }

    const subscription = _tableData.value.find(
      (data) => data.subscriptionId === selectedSubscriptionIdValue,
    );

    if (!subscription) {
      return null;
    }

    return subscription;
  });
  const filterBy = computed(() => _filterBy.value);
  const hasFilteredItems = computed(
    () =>
      !!_filterBy.value &&
      Object.values(_filterBy.value).some(
        (filterItem) => !!filterItem.checked.length,
      ),
  );
  const selectedRadio = computed(() => _selectedRadio.value);

  // Actions
  const setSelectedRadio = (selectedRadio: RadioDeploymentType) => {
    _selectedRadio.value = selectedRadio;
  };

  const setFilterBy = (filterBy: FilterCardsListModel) => {
    _filterBy.value = filterBy;
  };

  const setSelectedSubscriptionId = (selectedSubscriptionId: number | null) => {
    _selectedSubscriptionId.value = selectedSubscriptionId;
  };

  const setSubscription = async () => {
    const selectedSubscriptionIdValue = _selectedSubscriptionId.value;
    if (!selectedSubscriptionIdValue) {
      return;
    }

    _isLoadingSubscription.value = true;
    try {
      _subscriptionDetails.value = await partnerSubscription(
        selectedSubscriptionIdValue,
      );
    } catch (e) {
      logger.error(`Error: ${e}`);
    }
    _isLoadingSubscription.value = false;
  };

  const resetTableData = async () => {
    _tableData.value = [];
  };

  const setTableData = async () => {
    _isLoading.value = true;
    try {
      const subscriptions = await partnerSubscriptions(
        usePartnerStore().currentPartnerId,
      );

      const subscriptionsForUsage = subscriptions.subscriptions
        .filter(
          (subscription) =>
            subscription.subscriptionPartnershipStatus !==
              RenewalPeriodValues.TERMINATED &&
            subscription.deploymentType.toLowerCase() === "saas" &&
            subscription.isUsageViewEnabled,
        )
        .map((value) => value.subscriptionId);

      // if no subscriptions for usage, return empty array
      const subscriptionsUsage =
        subscriptionsForUsage.length > 0
          ? await entitlementPerSubscriptionUsage({
              subscriptions: subscriptionsForUsage,
              entitlements: ["TRAFFIC", "STORAGE"],
              startDate: DateTime.now().startOf("month").toFormat("yyyy-MM-dd"),
              endDate: DateTime.now().toFormat("yyyy-MM-dd"),
            })
          : [];

      _tableData.value = subscriptions.subscriptions
        .map((subscription) => ({
          companyName: subscription.customerCompanyName,
          subscriptionId: subscription.subscriptionId,
          deploymentType: subscription.deploymentType,
          subscriptionType: subscription.subscriptionType,
          primaryContactEmail: subscription.customerContactEmail,
          subscriptionStatus: subscription.subscriptionPartnershipStatus,
          monthlyPercentage: calcPercentage(subscription, subscriptionsUsage),
          region: subscription.customerRegion,
          paymentType: subscription.paymentPlan,
          billingType: subscription.billingType,
          derivedEndDate: subscription.derivedEndDate,
          billingView: subscription.isUsageViewEnabled,
        }))
        .sort((a, b) => {
          const companyComparison = a.companyName.localeCompare(b.companyName);

          return companyComparison !== 0
            ? companyComparison
            : a.subscriptionId - b.subscriptionId;
        });

      analyticsService.setPartnerSubscriptionsProperties();
    } catch (e) {
      logger.error(`Error: ${e}`);
      useNotifications().error(messagesService.app_something_went_wrong);
    }
    _isLoading.value = false;
  };

  return {
    tableData,
    totalSubscriptions,
    isLoading,
    isLoadingSubscription,
    subscriptionDetails,
    saasSubscriptions,
    activeSaasSubscriptions,
    selfHostedSubscriptions,
    activeSelfHostedSubscriptions,
    subscriptionTypes,
    selectedSubscriptionId,
    selectedTableSubscription,
    filterBy,
    hasFilteredItems,
    selectedRadio,
    setSelectedRadio,
    setFilterBy,
    setSelectedSubscriptionId,
    setSubscription,
    resetTableData,
    setTableData,
  };
});
